import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';

import {
  getBooking, signAgreement, openPaymentIntent, confirmPayment,
  PIPELINE_STAGES, type ManagedBooking,
} from '../../lib/managedVenues';
import { getStripe } from '../../lib/stripe';
import { ApiError } from '../../lib/apiClient';
import { formatMoney, formatDateShort, formatDateTime } from '../../lib/format';
import './ManagedVenues.css';
import './VenueWizard.css';

/**
 * Where a booking stands, and what the organizer can do next.
 *
 * Reachable with the booking id alone, because the organizer may have no account. The id
 * is a v4 uuid and is the capability: it is not guessable and nothing here lists other
 * bookings.
 *
 * Each action appears only at the stage where it means something. A pay button before the
 * agreement is signed would be an action the engine refuses, and offering it would teach
 * an organizer that the pipeline order is advisory.
 */
/**
 * The card step.
 *
 * Two phases, because the pipeline must not advance on anything the client says. Stripe
 * confirms the card here, then the engine retrieves that intent and checks it succeeded,
 * belongs to this booking, and covers the full amount before moving anything.
 *
 * Card details never touch this origin: they go from the Stripe iframe to Stripe.
 */
function PaymentForm({ booking, clientSecret, onPaid, onCancel }: {
  booking: ManagedBooking; clientSecret: string;
  onPaid: (paymentIntentId: string) => void; onCancel: () => void;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!stripe || !elements) return;
    setBusy(true);
    setError(null);

    const { error: submitError } = await elements.submit();
    if (submitError) { setError(submitError.message ?? 'Check your card details.'); setBusy(false); return; }

    const { error: confirmError, paymentIntent } = await stripe.confirmPayment({
      elements,
      clientSecret,
      // Handled here rather than by redirect, so the engine can verify before the page
      // changes and the organizer is never told they have paid before it has.
      redirect: 'if_required',
    });
    if (confirmError) { setError(confirmError.message ?? 'Payment failed.'); setBusy(false); return; }
    if (!paymentIntent) { setError('Payment did not complete.'); setBusy(false); return; }

    onPaid(paymentIntent.id);
  }

  return (
    <form className="wz-panel" onSubmit={submit}>
      <h2 className="wz-panel__title">Payment</h2>
      <p className="wz-hint">
        {formatMoney(booking.organizerCostCents)} due now. Your dates are confirmed and
        technical prep begins once payment clears.
      </p>
      <PaymentElement options={{ layout: 'tabs' }} />
      {error && <p className="adm-error" role="alert">{error}</p>}
      <p className="wz-hint">
        Card details are entered directly with Stripe. Voleto never sees or stores them.
      </p>
      <div className="wz-actions">
        <button type="button" className="button button--ghost" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
        <button type="submit" className="button button--primary" disabled={!stripe || busy}>
          {busy ? 'Processing…' : `Pay ${formatMoney(booking.organizerCostCents)}`}
        </button>
      </div>
    </form>
  );
}

export function BookingPipeline() {
  const { id = '' } = useParams();
  const [booking, setBooking] = useState<ManagedBooking | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [modal, setModal] = useState<null | 'sign' | 'pay'>(null);
  const [typedName, setTypedName] = useState('');
  const [busy, setBusy] = useState(false);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const stripePromise = getStripe();

  const load = useCallback(() => {
    getBooking(id)
      .then((b) => { setBooking(b); setError(null); })
      .catch((err: unknown) =>
        setError(err instanceof ApiError ? err.message : 'Could not load this booking.'));
  }, [id]);

  useEffect(load, [load]);

  async function run(fn: () => Promise<unknown>) {
    setBusy(true);
    setError(null);
    try { await fn(); load(); } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That did not work.');
    } finally { setBusy(false); }
  }

  if (error && !booking) {
    return <div className="mv-page"><div className="alert alert--error" role="alert">{error}</div></div>;
  }
  if (!booking) return <div className="mv-page"><p className="mv-state">Loading…</p></div>;

  const stage = booking.pipelineStage;

  return (
    <div className="mv-page">
      <header className="wz-head">
        <p className="wz-head__eyebrow mono">Booking {booking.id.slice(0, 8)}</p>
        <h1 className="wz-head__title display">{booking.eventName}</h1>
        <p className="wz-head__sub">
          {booking.eventStartDate === booking.eventEndDate
            ? formatDateShort(booking.eventStartDate)
            : `${formatDateShort(booking.eventStartDate)} to ${formatDateShort(booking.eventEndDate)}`}
          {' · '}{booking.attendance} expected
        </p>
      </header>

      {error && <div className="alert alert--error" role="alert">{error}</div>}

      <ol className="pl-track">
        {PIPELINE_STAGES.map((label, i) => (
          <li key={label}
            className={`pl-node${i === stage ? ' is-current' : ''}${i < stage ? ' is-done' : ''}`}>
            <span className="pl-node__dot mono">{i + 1}</span>
            <span className="pl-node__label">{label}</span>
          </li>
        ))}
      </ol>

      <section className="wz-panel">
        <h2 className="wz-panel__title">Summary</h2>
        <dl className="wz-review">
          <div><dt>Status</dt><dd>{booking.status.replace(/_/g, ' ')}</dd></div>
          <div><dt>Due before the event</dt>
            <dd className="mono">{formatMoney(booking.organizerCostCents)}</dd></div>
          <div><dt>Admission</dt>
            <dd>{booking.admissionType === 'ticketed'
              ? `Ticketed at ${formatMoney(booking.ticketPriceCents)}` : 'Free or RSVP'}</dd></div>
          <div><dt>Agreement</dt>
            <dd>{booking.signedAt
              ? `Signed by ${booking.signedByName} on ${formatDateTime(booking.signedAt)}`
              : 'Not signed'}</dd></div>
          <div><dt>Payment</dt>
            <dd>{booking.paymentCompletedAt
              ? `Received ${formatDateTime(booking.paymentCompletedAt)}` : 'Not paid'}</dd></div>
          {booking.status === 'settled' && (
            <div><dt>Settled to you</dt>
              <dd className="mono">{formatMoney(booking.netPayoutCents)}</dd></div>
          )}
        </dl>

        <div className="wz-actions">
          {!booking.signedAt && booking.status !== 'declined' && (
            <button type="button" className="button button--primary" onClick={() => setModal('sign')}>
              Sign agreement
            </button>
          )}
          {booking.signedAt && !booking.paymentCompletedAt && (
            <button type="button" className="button button--primary" disabled={busy}
              onClick={() => void run(async () => {
                // Opens the intent, then the card form mounts against its secret.
                const intent = await openPaymentIntent(booking.id);
                setClientSecret(intent.clientSecret);
                setModal('pay');
              })}>
              Pay {formatMoney(booking.organizerCostCents)}
            </button>
          )}
        </div>
      </section>

      {modal === 'sign' && (
        <section className="wz-panel">
          <h2 className="wz-panel__title">Facility Use and Event Production Agreement</h2>
          <p className="wz-hint">
            Signing commits you to the reserved dates, the fees above, and exclusive
            ticketing through Voleto. The full agreement is generated on signature and
            retained with this booking.
          </p>
          <label className="field wiz-field--wide">
            <span className="field__label">Type your full legal name to sign *</span>
            <input className="field__input" value={typedName}
              onChange={(e) => setTypedName(e.target.value)} />
          </label>
          <p className="wz-esign mono">
            Electronic signature under the U.S. E-SIGN Act. Your name, the time and your IP
            address are recorded with the document.
          </p>
          <div className="wz-actions">
            <button type="button" className="button button--ghost" onClick={() => setModal(null)}>
              Cancel
            </button>
            <button type="button" className="button button--primary"
              disabled={busy || typedName.trim().length < 2}
              onClick={() => void run(async () => {
                await signAgreement(booking.id, typedName.trim());
                setModal(null);
              })}>
              {busy ? 'Signing…' : 'Sign agreement'}
            </button>
          </div>
        </section>
      )}

      {modal === 'pay' && clientSecret && stripePromise && (
        <Elements stripe={stripePromise} options={{ clientSecret, appearance: { theme: 'night' } }}>
          <PaymentForm
            booking={booking}
            clientSecret={clientSecret}
            onCancel={() => { setModal(null); setClientSecret(null); }}
            onPaid={(paymentIntentId) => void run(async () => {
              // The engine verifies this with Stripe before advancing anything.
              await confirmPayment(booking.id, paymentIntentId);
              setClientSecret(null);
              setModal(null);
            })}
          />
        </Elements>
      )}

      {modal === 'pay' && !stripePromise && (
        <section className="wz-panel">
          <p className="adm-error" role="alert">
            Payments are not configured for this environment.
          </p>
        </section>
      )}
    </div>
  );
}
